import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useClient } from 'urql';

import { OrderTypeValues } from '@sg/shared-be-features-ordering';

import { storePendingPaymentInfo } from '@order/features-ordering';

import {
  CreateOrderPaymentIntentDocument,
  type CreateOrderPaymentIntentMutation,
  type CreateOrderPaymentIntentMutationVariables,
} from '../../GraphQL/CreatePaymentIntent.generated';

/**
 * Creates a payment intent for the provided order.
 */
export const useCreatePaymentIntent = () => {
  const urqlClient = useClient();
  const { formatMessage } = useIntl();

  return useCallback(
    async (input: CreateOrderPaymentIntentMutationVariables['input']) => {
      const response = await urqlClient
        .mutation<CreateOrderPaymentIntentMutation>(
          CreateOrderPaymentIntentDocument,
          { input },
          { requestPolicy: 'network-only' },
        )
        .toPromise();

      const paymentIntentId = response.data?.createOrderPaymentIntent?.id;
      const clientSecret = response.data?.createOrderPaymentIntent?.secret;

      if (!paymentIntentId || !clientSecret) {
        // Caught by the Stripe integration and shown to the customer.
        throw new Error(formatMessage(messages.error));
      }

      // We need to set the order id in local storage after payment intent creation.
      // This will be used to show order fulfillment feedback on the home screen.
      await storePendingPaymentInfo(input.orderId, OrderTypeValues.GravyOrder);

      return {
        paymentIntentId,
        clientSecret,
      };
    },
    [urqlClient, formatMessage],
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  error: {
    defaultMessage: 'Something went wrong setting up your payment.',
    description: 'Stripe Payment | Create Payment Intent Error',
  },
});
